<template>
  <v-dialog
    v-model="dialog"
    max-width="800px"
    max-height="90vh"
    content-class="my-custom-dialog"
    style="border-radius: 20px !important"
    scrollable>
    <v-card class="d-flex flex-column" style="height: 85vh;">
      <v-card-title style="background-color: #1577da; font-weight: bold; color: white; flex-shrink: 0;">
        <span class="text-h5" style="font-weight: bold">Detalji dokumenta</span>
        <v-spacer></v-spacer>
        <v-btn icon @click="dialog = false" color="white">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="flex-grow-1" style="overflow-y: auto; padding: 0;">
        <div v-if="loading" class="text-center pa-8">
          <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
          <div class="mt-4">Učitavam detalje dokumenta...</div>
        </div>

        <div v-else-if="error" class="text-center pa-8">
          <v-icon color="error" size="64">mdi-alert-circle</v-icon>
          <div class="mt-4 text-error">{{ error }}</div>
          <v-btn color="primary" @click="fetchDocumentDetails" class="mt-4">
            Pokušaj ponovno
          </v-btn>
        </div>

        <v-container v-else-if="documentDetails" class="pa-6">
          <v-row>
            <v-col cols="12" md="6">
              <h4>Osnovne informacije</h4>
              <v-divider class="mb-3"></v-divider>
              <v-list dense>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>ID dokumenta</v-list-item-title>
                    <v-list-item-subtitle>{{ documentDetails.id }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>Document ID</v-list-item-title>
                    <v-list-item-subtitle>{{ documentDetails.documentId || 'N/A' }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>Tip dokumenta</v-list-item-title>
                    <v-list-item-subtitle>{{ documentDetails.documentType || 'N/A' }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-col>

            <v-col cols="12" md="6">
              <h4>Informacije o strankama</h4>
              <v-divider class="mb-3"></v-divider>
              <v-list dense>
                <v-list-item v-if="documentDetails.supplierPartyName">
                  <v-list-item-content>
                    <v-list-item-title>Dobavljač</v-list-item-title>
                    <v-list-item-subtitle>{{ documentDetails.supplierPartyName }}</v-list-item-subtitle>
                    <v-list-item-subtitle v-if="documentDetails.supplierPartyVATId">
                      OIB: {{ documentDetails.supplierPartyVATId }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item v-if="documentDetails.customerPartyName">
                  <v-list-item-content>
                    <v-list-item-title>Kupac</v-list-item-title>
                    <v-list-item-subtitle>{{ documentDetails.customerPartyName }}</v-list-item-subtitle>
                    <v-list-item-subtitle v-if="documentDetails.customerPartyVATId">
                      OIB: {{ documentDetails.customerPartyVATId }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" class="text-center mb-3">
              <div class="text-caption">Status dokumenta</div>
              <v-chip :color="getStatusColor(documentDetails.status)" dark class="text-h6 px-4 py-2">
                {{ getStatusText(documentDetails.status) }}
              </v-chip>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <v-row no-gutters>
                <v-col cols="3" class="px-2">
                  <div class="text-caption">Datum izdavanja</div>
                  <div class="text-body-2">{{ formatDate(documentDetails.issuedOn) }}</div>
                </v-col>
                <v-col cols="3" class="px-2">
                  <div class="text-caption">Datum unosa</div>
                  <div class="text-body-2">{{ formatDate(documentDetails.insertedOn) }}</div>
                </v-col>
                <v-col cols="3" class="px-2">
                  <div class="text-caption">Datum izmjene</div>
                  <div class="text-body-2">{{ formatDate(documentDetails.modifiedOn) }}</div>
                </v-col>
                <v-col cols="3" class="text-right px-2 d-flex flex-column align-end" v-if="documentDetails.amount">
                  <div class="text-caption">Ukupan iznos</div>
                  <div class="text-h6 font-weight-bold primary--text">{{ formatCurrency(documentDetails.amount, documentDetails.currency) }}</div>
                </v-col>
              </v-row>
            </v-col>
          </v-row>

          <v-row v-if="documentDetails.statusHistory && documentDetails.statusHistory.length">
            <v-col cols="12">
              <h4>Povijest statusa</h4>
              <v-divider class="mb-3"></v-divider>
              <v-timeline dense>
                <v-timeline-item
                  v-for="(status, index) in documentDetails.statusHistory.slice().reverse()"
                  :key="index"
                  :color="getStatusColor(status.status)"
                  small
                >
                  <div class="py-4">
                    <h4 class="text-h6 mb-1">
                      {{ status.statusText || getStatusText(status.status) }}
                    </h4>
                    <div class="text-body-2 mb-2">
                      {{ formatDate(status.createdOn || status.timestamp) }}
                    </div>
                    <div v-if="status.notes" class="text-body-2">
                      {{ status.notes }}
                    </div>
                  </div>
                </v-timeline-item>
              </v-timeline>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>

      <v-card-actions class="pr-10 pl-10" style="flex-shrink: 0;">
        <v-row dense style="margin-top: 1rem; margin-bottom: 1rem">
          <v-spacer></v-spacer>
          <v-btn class="btn infoButton" @click="dialog = false">Zatvori</v-btn>
        </v-row>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { DateTime } from 'luxon'
import { eracunApi } from '@/api/eracun'
import state from '@/state'

export default {
  name: 'DocumentDetailDialog',
  props: {
    value: {
      type: Boolean,
      default: false
    },
    document: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      loading: false,
      error: null,
      documentDetails: null
    }
  },
  computed: {
    dialog: {
      get () {
        return this.value
      },
      set (val) {
        this.$emit('input', val)
      }
    }
  },
  watch: {
    value (newVal) {
      if (newVal && this.document) {
        // Ako dokument već ima sve potrebne detalje, koristi ga direktno
        if (this.document.documentId && this.document.status !== undefined &&
            this.document.supplierPartyName && this.document.customerPartyName) {
          // Dokument ima sve potrebne podatke, prikaži direktno
          this.documentDetails = this.document
          this.loading = false
        } else if (this.document.id) {
          // Inače, dohvati podatke putem API-ja ako ima id
          this.fetchDocumentDetails()
        } else {
          this.error = 'Dokument nema potrebne podatke'
        }
      } else if (!newVal) {
        this.documentDetails = null
        this.error = null
      }
    }
  },
  methods: {
    async fetchDocumentDetails () {
      this.loading = true
      this.error = null

      try {
        const companyId = state.getCurrentCompany().id
        const response = await eracunApi.getDocument(this.document.id, companyId)
        this.documentDetails = response
      } catch (error) {
        console.error('Error fetching document details:', error)
        this.error = 'Greška pri dohvaćanju detalja dokumenta'
      } finally {
        this.loading = false
      }
    },

    formatDate (dateString) {
      if (!dateString) return 'N/A'
      try {
        return DateTime.fromISO(dateString).setLocale('hr').toFormat('dd.MM.yyyy HH:mm')
      } catch (error) {
        return dateString
      }
    },
    formatCurrency (amount, currency = 'EUR') {
      if (amount === null || amount === undefined) return 'N/A'
      return new Intl.NumberFormat('hr-HR', {
        style: 'currency',
        currency: currency
      }).format(amount)
    },
    getStatusColor (status) {
      const statusColors = {
        1: 'blue',
        2: 'orange',
        3: 'green',
        4: 'red',
        5: 'purple',
        6: 'indigo',
        7: 'teal',
        8: 'brown',
        9: 'grey'
      }
      return statusColors[status] || 'grey'
    },
    getStatusText (status) {
      const statusTexts = {
        1: 'Na čekanju',
        2: 'Na slanju',
        3: 'Poslan',
        4: 'Isporučen',
        5: 'Odobren',
        6: 'Odbijen',
        7: 'Plaćen u potpunosti',
        8: 'Parcijalno plaćen',
        9: 'Neuspješna isporuka'
      }
      return statusTexts[status] || `Status ${status}`
    }
  }
}
</script>

<style scoped>
.v-dialog__content >>> .my-custom-dialog {
  position: absolute;
  top: 30px;
  border-radius: 25px !important;
}
</style>
